/**
 * Slovak catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Slovak at a mathematician's register. **Warrant → «Opodstatnenie»**
 * (justification — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / few / many / other (the neuter «obmedzenie» declined: 1 obmedzenie ·
 * 2–4 obmedzenia · 5+ obmedzení), so `frameActive` declares exactly those (plus an explicit
 * `=0`). Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay
 * fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const SK_CATALOG = {
    locale: 'sk',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Skúmanie',
        [P2.sectionClaims]: 'Tvrdenia',
        [P2.sectionIntegrity]: 'Integrita',
        [P2.axisWarrantLabel]: 'Opodstatnenie',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirické',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definícia} interp {Interpretácia} testable {Testovateľné} tested {Testované} refuted {Vyvrátené} program {Výskumný program} philos {Filozofické} other {Neznáme}}',
        [P2.axisModalLabel]: 'Modálne',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Úroveň sily {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Preskúmateľné',
        [P2.stateRefuted]: 'Vyvrátené',
        [P2.stateOutside]: 'Mimo rozsahu',
        [P2.verdictConflict]: 'V konflikte s {constraints}',
        [P2.verdictOutside]: 'Vyžaduje {missing}',
        [P2.lossRegisterPlain]: 'Zjednodušené do bežného jazyka — časť presnosti je vynechaná.',
        [P2.lossRegisterKid]: 'Zjednodušené pre mladších čitateľov — mnoho podrobností je vynechaných.',
        [P2.lossProfileDetail]: 'Úplný profil Opodstatnenie / Empirické / Modálne je tu zhrnutý.',
        [P2.lossConflictDetail]: 'Podrobnosti konfliktu sú v tomto zobrazení skrátené.',
        [P2.laneVerified]: 'Overené',
        [P2.laneUnverified]: 'Neoverené',
        [P2.laneConflict]: 'Konflikt preukázaný',
        [P2.constraintLogic]: 'Logika',
        [P2.constraintLightBends]: 'Svetlo sa ohýba v gravitačnom poli',
        [P2.constraintParallelPostulate]: 'Postulát o rovnobežkách',
        [P2.claimScalarGravity]: 'Skalárna teória gravitácie',
        [P2.claimNonEuclidean]: 'Neeuklidovská geometria',
        [P2.claimSubstrateSr]: 'Substrátová interpretácia špeciálnej relativity',
        [P2.edgeExtObservation]: 'Vonkajšie pozorovanie',
        [P2.edgeResourceSample]: 'Zdieľaný výpočtový zdroj',
        [P2.edgeHeuristicSample]: 'Heuristické usmernenie',
        [P2.edgeSharedSample]: 'Spoločný základ',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Obmedzenia',
        [P2.sectionEdges]: 'Hrany',
        [P2.sectionFrame]: 'Rámec',
        [P2.sectionExposure]: 'Expozícia',
        [P2.statusAdoptedFormal]: 'Prijaté (formálne)',
        [P2.statusAdoptedInterpretive]: 'Prijaté (interpretačné)',
        [P2.statusSuspended]: 'Pozastavené',
        [P2.edgeEffectConstraint]: 'Tvorí obmedzenie (môže vyvrátiť)',
        [P2.edgeEffectResource]: 'Zdroj — bez vplyvu na verdikty',
        [P2.edgeEffectHeuristic]: 'Heuristika — bez vplyvu na verdikty',
        [P2.edgeEffectShared]: 'Spoločný základ — bez vplyvu na verdikty',
        [P2.frameActive]: '{count, plural, =0 {Žiadne aktívne obmedzenia} one {# aktívne obmedzenie} few {# aktívne obmedzenia} many {# aktívneho obmedzenia} other {# aktívnych obmedzení}}',
        [P2.exposureNone]: 'Žiadne interpretačné závislosti',
        [P2.exposureDep]: 'Závisí od {id}',
        [P2.laneTrackMachine]: 'Stroj',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Komunita',
        [P2.expertAudited]: 'Auditované',
        [P2.expertInProgress]: 'Prebieha',
        [P2.expertNotReviewed]: 'Neskontrolované',
        [P2.communityFlags]: 'Existujú označenia',
        [P2.communityNone]: 'Žiadne označenia',
        [P2.controlChallenge]: 'Spochybniť',
        [P2.controlActivate]: 'Aktivovať',
        [P2.controlMakeInterpretive]: 'Urobiť interpretačným',
        [P2.controlImportEdge]: 'Importovať hranu',
        [P2.controlPromote]: 'Povýšiť',
        [P2.controlUndo]: 'Späť',
        [P2.controlRedo]: 'Znova',
        [P2.controlRegister]: 'Zaregistrovať',
        [P2.promoteRejected]: 'Povýšenie zamietnuté',
        [P2.promoteAccepted]: 'Povýšenie prijaté',
        [P2.promoteReasonRefutationTerminal]: 'Dôkaz nemôže napraviť vyvrátenie.',
        [P2.promoteReasonKeystone]: 'Kľúčové povýšenie vyžaduje empirickú oporu.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empirický druh nie je pevne daný.',
        [P2.promoteReasonModalNotFixed]: 'Modálny stupeň nie je pevne daný.',
        [P2.promoteReasonWarrantNotImproved]: 'Stupeň opodstatnenia sa nezlepšil.',
        [P2.evidenceComputation]: "{engine} vypočítal {op} = {result} pri {config}",
        [P2.lossNumericRounding]: "Výsledok zaokrúhlený na {dp} desatinných miest",
    },
};
//# sourceMappingURL=sk.js.map